/**
 * mc-product-media.js — Manifesto (task 0032)
 *
 * Video support for the media gallery of the three purchase pages:
 * PDP (main-product-manifesto), Harmonizacao (main-product-harmonizacao)
 * and Bundle (main-product-bundle-cervejas).
 *
 * Template-only asset (DP-15): loaded by those three sections, never by
 * theme.liquid. Zero diff in global.js, mc-product-page.js,
 * mc-harmonizacao.js and mc-bundle-cervejas.js.
 *
 * What it does
 *   1. Delegated activation of media thumbs, on `document`, so it also
 *      covers the bundle thumbs injected at runtime by the 0028 app
 *      hydration.
 *   2. Slide activation: hosted <video> plays muted/looped with native
 *      controls; `external_video` hydrates its iframe only on click.
 *   3. Pause + rewind of the outgoing slide (DP-6e) — never two audios.
 *   4. DP-11 image fix: `srcset` is written (or removed) BEFORE `src`, and
 *      `alt` follows the displayed image.
 *   5. Conditional, deferred autostart (DP-5).
 *   6. Loading and error states, including the 8s iframe timeout.
 *   7. One `product_video_play` dataLayer event per media per pageview,
 *      with no personal data (DP-18).
 *
 * COEXISTENCE WITH THE LEGACY GALLERY JS (decision, DP-10)
 *   The new thumbs keep their legacy hook (`data-mc-pdp-thumb` /
 *   `data-mc-bundle-thumb`) byte for byte, so the legacy listeners still
 *   fire. No `stopImmediatePropagation`, no capture phase: the legacy
 *   handlers are bound to the thumb (PDP) or to the section (bundle), while
 *   this one is delegated on `document`, so in the bubble phase this one
 *   always runs LAST and its DOM writes are the final state.
 *   The legacy effect (`removeAttribute('srcset')` + `src = data-full` +
 *   `.is-active`) is a strict subset of what is done here, and for a video
 *   thumb `data-full` is the poster of that very video — which is exactly
 *   what has to sit behind the slide. This is the least invasive option
 *   that keeps the legacy files at zero diff.
 *
 * ROUTING BY ABSENCE (DP-10)
 *   A thumb WITHOUT `data-media-type` takes the image path. Component
 *   thumbs and app-injected thumbs (raw URLs, no type metadata) therefore
 *   keep working through the path that already works today, and never fall
 *   into `undefined`.
 *
 * DEFERRED MEDIA (DP-16)
 *   The `<deferred-media>` custom element from Dawn's global.js is reused —
 *   it is already loaded on every page and its contract is satisfiable
 *   here: a poster whose id starts with `Deferred-Poster-` and a <template>
 *   whose first element child is the <iframe>. If `loadContent` is not a
 *   function (element not upgraded, global.js blocked, load order changed)
 *   the template is cloned manually. Zero modification to global.js.
 */

(function () {
  'use strict';

  var IFRAME_TIMEOUT_MS = 8000;
  var DESKTOP_MIN_WIDTH = 750;
  var IDLE_FALLBACK_MS = 1200;

  /* One telemetry event per media per pageview (DP-18). */
  var trackedMedia = {};

  /* ─── Environment guards ─── */

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isDataSaving() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData === true) return true;
    var type = conn.effectiveType || '';
    return type === '2g' || type === 'slow-2g';
  }

  /* ─── DOM helpers ─── */

  function closestOf(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function rootOf(el) {
    return closestOf(el, '[data-section-id]') || document;
  }

  function hostOf(root) {
    return root.querySelector('[data-mc-media-host]');
  }

  function heroImageOf(root) {
    return root.querySelector('[data-mc-pdp-main], [data-mc-bundle-main], [data-mc-media-main]');
  }

  function slidesOf(root) {
    return root.querySelectorAll('[data-mc-media-slide]');
  }

  function slideFor(root, mediaId) {
    if (!mediaId) return null;
    var list = slidesOf(root);
    for (var i = 0; i < list.length; i++) {
      if (list[i].getAttribute('data-media-id') === String(mediaId)) return list[i];
    }
    return null;
  }

  function stateOf(root) {
    var host = hostOf(root);
    if (!host) return null;
    if (!host.mcMediaState) {
      host.mcMediaState = {
        activeId: host.getAttribute('data-mc-media-featured-id') || '',
        userActed: false
      };
    }
    return host.mcMediaState;
  }

  /* ─── Telemetry (DP-18) — no PII, ever ─── */

  function pushPlayEvent(root, slide, trigger) {
    var host = hostOf(root);
    if (!host || !slide) return;
    var surface = host.getAttribute('data-mc-media-surface') || '';
    var mediaId = slide.getAttribute('data-media-id') || '';
    var key = surface + '|' + mediaId;
    if (trackedMedia[key]) return;
    trackedMedia[key] = true;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'product_video_play',
      surface: surface,
      media_type: slide.getAttribute('data-media-type') || '',
      media_id: mediaId,
      product_id: host.getAttribute('data-mc-media-product-id') || '',
      product_handle: host.getAttribute('data-mc-media-product-handle') || '',
      trigger: trigger
    });
  }

  /* ─── Loading / error states ─── */

  function startLoading(slide) {
    slide.classList.add('is-loading');
    slide.setAttribute('aria-busy', 'true');
  }

  function stopLoading(slide) {
    slide.classList.remove('is-loading');
    slide.removeAttribute('aria-busy');
  }

  function showError(slide) {
    slide.classList.add('is-error');
    var panel = slide.querySelector('[data-mc-media-error]');
    if (panel) panel.hidden = false;
  }

  function clearError(slide) {
    slide.classList.remove('is-error');
    var panel = slide.querySelector('[data-mc-media-error]');
    if (panel) panel.hidden = true;
  }

  /* ─── Hosted video ─── */

  function normalizeVideo(video) {
    /* Belt and braces: these attributes are already emitted by video_tag,
       but the iOS/Safari autoplay policy rejects `.play()` without them and
       a silent rejection would look like a dead player. */
    video.muted = true;
    if (!video.hasAttribute('playsinline')) video.setAttribute('playsinline', '');
    if (!video.hasAttribute('controls')) video.setAttribute('controls', '');
    if (!video.hasAttribute('preload')) video.setAttribute('preload', 'none');
    /* DP-6c: a perpetual loop in peripheral vision is the worst case for
       vestibular sensitivity — reduced motion drops the loop entirely. */
    if (prefersReducedMotion()) video.removeAttribute('loop');
  }

  function bindVideo(slide, video) {
    if (video.mcMediaBound) return;
    video.mcMediaBound = true;
    video.addEventListener('loadeddata', function () {
      stopLoading(slide);
    });
    video.addEventListener('playing', function () {
      stopLoading(slide);
      slide.classList.remove('is-paused');
    });
    video.addEventListener('error', function () {
      stopLoading(slide);
      showError(slide);
    });
  }

  /* ─── external_video (DP-8 / DP-16) ─── */

  function manualHydrate(deferred, focus) {
    var template = deferred.querySelector('template');
    if (!template || !template.content || !template.content.firstElementChild) return;
    var node = template.content.firstElementChild.cloneNode(true);
    deferred.setAttribute('loaded', 'true');
    deferred.appendChild(node);
    if (focus && typeof node.focus === 'function') node.focus();
  }

  function hydrateExternal(slide, focus) {
    var deferred = slide.querySelector('[data-mc-media-deferred]');
    if (!deferred) return;

    if (!deferred.getAttribute('loaded')) {
      var loaded = false;
      if (typeof deferred.loadContent === 'function') {
        try {
          deferred.loadContent(!!focus);
          loaded = !!deferred.getAttribute('loaded');
        } catch (err) {
          loaded = false;
        }
      }
      if (!loaded) manualHydrate(deferred, focus);
    }

    var frame = deferred.querySelector('iframe');
    if (!frame) {
      showError(slide);
      return;
    }

    startLoading(slide);
    if (!frame.mcMediaBound) {
      frame.mcMediaBound = true;
      frame.addEventListener('load', function () {
        window.clearTimeout(slide.mcMediaTimer);
        stopLoading(slide);
        clearError(slide);
      });
    }
    /* A cross-origin iframe never reports an error, so the only honest
       signal is a timeout (F0 2.3): 8s then the fallback panel + link. */
    window.clearTimeout(slide.mcMediaTimer);
    slide.mcMediaTimer = window.setTimeout(function () {
      if (slide.classList.contains('is-loading')) {
        stopLoading(slide);
        showError(slide);
      }
    }, IFRAME_TIMEOUT_MS);
  }

  /* ─── Slide activation / deactivation ─── */

  function deactivateSlide(slide) {
    window.clearTimeout(slide.mcMediaTimer);

    var video = slide.querySelector('video');
    if (video) {
      /* DP-6e: pause AND rewind — never a video playing off screen. */
      try {
        video.pause();
        video.currentTime = 0;
      } catch (err) {
        /* Some browsers throw on currentTime before metadata; harmless. */
      }
    }

    var deferred = slide.querySelector('[data-mc-media-deferred]');
    if (deferred) {
      /* The embed runs with enablejsapi=0, so postMessage pause is not
         reliable. Dropping the iframe is deterministic: it guarantees no
         background audio and keeps the third-party connection alive only
         while the user is actually watching. A later click re-hydrates
         from the <template>, still under an explicit user action. */
      var frame = deferred.querySelector('iframe');
      if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
      deferred.removeAttribute('loaded');
    }

    clearError(slide);
    stopLoading(slide);
    slide.classList.remove('is-active', 'is-paused');
    slide.hidden = true;
  }

  function openSlide(root, slide, options) {
    slide.hidden = false;
    slide.classList.add('is-active');

    var heroImage = heroImageOf(root);
    /* `visibility`, never `display` — hiding the bundle hero <img> with
       display:none would collapse the height of the whole hero. */
    if (heroImage) {
      /* Sizing fix (owner QA 2026-08-03): the LEGACY thumb handler runs
         first in the bubble and, on a video thumb, writes the video's
         poster into the hero <img>. On the bundle the hero is in-flow
         with `height:auto`, so a portrait poster (e.g. 604x1080) stretches
         the whole box and the video renders far larger than the photos.
         Restoring the last IMAGE the hero showed undoes that write: the
         box keeps the photo ratio and the absolute slide fits it. */
      restoreHeroImage(root, heroImage);
      heroImage.style.visibility = 'hidden';
    }

    var type = slide.getAttribute('data-media-type');

    if (type === 'external_video') {
      hydrateExternal(slide, !!options.focus);
    } else {
      var video = slide.querySelector('video');
      if (video) {
        normalizeVideo(video);
        bindVideo(slide, video);
        if (prefersReducedMotion()) {
          /* Parked on the poster with native controls, waiting for the
             user — no autoplay, no loop (DP-6c). */
          slide.classList.add('is-paused');
        } else {
          startLoading(slide);
          var attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(function () {
              /* iOS/Safari may still refuse: swallow it, show the paused
                 state, leave nothing in the console (DP-6b). */
              stopLoading(slide);
              slide.classList.add('is-paused');
            });
          }
        }
        if (options.focus && typeof video.focus === 'function') video.focus();
      }
    }

    pushPlayEvent(root, slide, options.trigger || 'click');
  }

  /* ─── Image path + DP-11 ─── */

  /* Last image state of the hero <img>, captured lazily on first use (the
     server-rendered featured image) and refreshed on every image
     activation. Used by openSlide to undo the legacy handler writing a
     video poster into the in-flow hero (sizing fix, owner QA 2026-08-03). */
  function rememberHeroImage(root, image) {
    var state = stateOf(root);
    if (!state) return;
    state.heroSrc = image.getAttribute('src');
    state.heroSrcset = image.getAttribute('srcset');
    state.heroAlt = image.getAttribute('alt');
  }

  function restoreHeroImage(root, image) {
    var state = stateOf(root);
    if (!state || !state.heroSrc) return;
    /* srcset before src — same ordering contract as applyImage (DP-11). */
    if (state.heroSrcset) {
      image.setAttribute('srcset', state.heroSrcset);
    } else {
      image.removeAttribute('srcset');
    }
    if (image.getAttribute('src') !== state.heroSrc) image.src = state.heroSrc;
    if (state.heroAlt !== null) image.alt = state.heroAlt;
  }

  function captureHeroImageOnce(root) {
    var state = stateOf(root);
    if (!state || state.heroSrc) return;
    var image = heroImageOf(root);
    if (image) rememberHeroImage(root, image);
  }

  function applyImage(image, thumb) {
    var full = thumb.getAttribute('data-full');
    if (!full) return;

    /* DP-11 — the whole point: with `srcset` present and `w` descriptors
       the browser picks from the source set and `src` stops being a
       candidate, so writing `src` alone changes nothing on screen. The
       srcset is written (or removed, for thumbs that carry no candidates —
       the app-injected bundle thumbs) BEFORE `src`. */
    var srcset = thumb.getAttribute('data-full-srcset');
    if (srcset) {
      image.setAttribute('srcset', srcset);
    } else {
      image.removeAttribute('srcset');
    }
    image.src = full;

    /* A gallery that swaps the image and keeps the previous alt lies to a
       screen reader — the a11y defect would exist only because we fixed
       the image one.
       Fix R-2 (/reviewer F5): thumbs with no `data-full-alt` — the ones
       injected by the 0028 app and the bundle component thumbs — used to
       leave the previous `alt` in place, which is exactly the lie above.
       Their `aria-label` already carries the right text (`it.title` /
       `comp.title`), so it is the natural fallback. Never write an empty
       string over a good alt: with neither source we leave the alt alone. */
    var alt = thumb.getAttribute('data-full-alt');
    if (alt === null) alt = thumb.getAttribute('aria-label');
    if (alt) image.alt = alt;
  }

  /* ─── Central play button of the hero (F0 2.4) ─── */

  function syncPlayButton(root) {
    var button = root.querySelector('[data-mc-media-play]');
    if (!button) return;
    var state = stateOf(root);
    var activeSlide = root.querySelector('[data-mc-media-slide].is-active');
    var owns = state && String(state.activeId) === String(button.getAttribute('data-media-id'));
    button.hidden = !!activeSlide || !owns;
  }

  function syncThumbs(root, activeThumb) {
    var thumbs = root.querySelectorAll('[data-mc-media-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      var on = thumbs[i] === activeThumb;
      thumbs[i].classList.toggle('is-active', on);
      if (on) {
        thumbs[i].setAttribute('aria-current', 'true');
      } else {
        thumbs[i].removeAttribute('aria-current');
      }
    }
  }

  function activate(root, mediaId, type, options) {
    options = options || {};
    var state = stateOf(root);
    var target = null;
    if (type === 'video' || type === 'external_video') target = slideFor(root, mediaId);

    var slides = slidesOf(root);
    for (var i = 0; i < slides.length; i++) {
      if (slides[i] !== target) deactivateSlide(slides[i]);
    }

    if (state) state.activeId = mediaId || '';

    if (target) {
      if (!target.classList.contains('is-active')) openSlide(root, target, options);
    } else {
      var heroImage = heroImageOf(root);
      if (heroImage) {
        heroImage.style.visibility = '';
        if (options.thumb) applyImage(heroImage, options.thumb);
        /* Keep the "last image shown" snapshot fresh so a later video
           activation restores THIS image, not an older one. */
        rememberHeroImage(root, heroImage);
      }
    }

    syncPlayButton(root);
  }

  /* ─── Interaction ─── */

  function handleThumb(thumb, viaKeyboard) {
    var root = rootOf(thumb);
    var state = stateOf(root);
    if (state) state.userActed = true;

    /* Routing by absence (DP-10): no `data-media-type` means image. */
    var type = thumb.getAttribute('data-media-type') || 'image';
    var mediaId = thumb.getAttribute('data-media-id') || '';

    syncThumbs(root, thumb);
    activate(root, mediaId, type, { thumb: thumb, focus: viaKeyboard, trigger: 'click' });
  }

  function handlePlayButton(button) {
    var root = rootOf(button);
    var state = stateOf(root);
    if (state) state.userActed = true;

    var mediaId = button.getAttribute('data-media-id');
    var type = button.getAttribute('data-media-type') || 'video';
    var thumb = null;
    var thumbs = root.querySelectorAll('[data-mc-media-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].getAttribute('data-media-id') === String(mediaId)) thumb = thumbs[i];
    }
    if (thumb) syncThumbs(root, thumb);
    activate(root, mediaId, type, { focus: true, trigger: 'click' });
  }

  function handlePoster(poster) {
    var slide = closestOf(poster, '[data-mc-media-slide]');
    if (!slide) return;
    var root = rootOf(slide);
    var state = stateOf(root);
    if (state) state.userActed = true;
    /* Dawn's own listener on the poster has already run (element listener
       before this delegated one), so this call is idempotent — it only
       adds the loading state, the timeout and the telemetry, or performs
       the manual hydration when the custom element never upgraded. */
    hydrateExternal(slide, true);
    pushPlayEvent(root, slide, 'click');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var thumb = target.closest('[data-mc-media-thumb], [data-mc-pdp-thumb], [data-mc-bundle-thumb]');
    if (thumb) {
      /* `detail === 0` on a button click means it came from the keyboard
         (Enter/Space) — DP-13.7 sends the focus to the player only then. */
      handleThumb(thumb, event.detail === 0);
      return;
    }

    var play = target.closest('[data-mc-media-play]');
    if (play) {
      handlePlayButton(play);
      return;
    }

    var poster = target.closest('[data-mc-media-poster]');
    if (poster) {
      handlePoster(poster);
      return;
    }

    var arrow = target.closest('[data-mc-media-arrow]');
    if (arrow) handleArrow(arrow);
  });

  /* Arrow keys move the focus inside the strip, with wrap-around; they do
     not activate (DP-13.6). Every thumb stays tabbable — no roving
     tabindex, no focus trap. */
  document.addEventListener('keydown', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var thumb = target.closest('[data-mc-media-thumb]');
    if (thumb && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      var siblings = thumb.parentElement
        ? thumb.parentElement.querySelectorAll('[data-mc-media-thumb]')
        : [];
      if (siblings.length < 2) return;
      var index = Array.prototype.indexOf.call(siblings, thumb);
      var next = event.key === 'ArrowRight' ? index + 1 : index - 1;
      if (next >= siblings.length) next = 0;
      if (next < 0) next = siblings.length - 1;
      siblings[next].focus();
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape' || event.key === 'Esc') {
      var slide = target.closest('[data-mc-media-slide]');
      if (!slide) return;
      var video = slide.querySelector('video');
      if (video) {
        try {
          video.pause();
        } catch (err) {
          /* nothing to do */
        }
      }
      /* Give the focus back to the owning thumb, but do NOT swap the
         active media — auto-swapping is disorienting (F0 1.3). */
      var root = rootOf(slide);
      var id = slide.getAttribute('data-media-id');
      var thumbs = root.querySelectorAll('[data-mc-media-thumb]');
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].getAttribute('data-media-id') === String(id)) {
          thumbs[i].focus();
          break;
        }
      }
    }
  });

  /* ─── Hero navigation arrows (owner request, QA 2026-08-03) ───
     The arrows walk the SAME strip the user sees (media thumbs + the
     bundle's component/app thumbs) by simulating a click on the
     neighbouring thumb — every existing path (DP-11 image swap, video
     activation, legacy coexistence, telemetry) is reused verbatim. */

  function galleryThumbsOf(root) {
    /* querySelectorAll never duplicates an element that matches more than
       one selector, and returns DOM order — exactly the visual order. */
    return root.querySelectorAll(
      '[data-mc-media-thumb], [data-mc-pdp-thumb], [data-mc-bundle-thumb]'
    );
  }

  function handleArrow(arrow) {
    var root = rootOf(arrow);
    var thumbs = galleryThumbsOf(root);
    if (thumbs.length < 2) return;

    var current = 0;
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].classList.contains('is-active')) {
        current = i;
        break;
      }
    }

    var dir = arrow.getAttribute('data-mc-media-arrow') === 'prev' ? -1 : 1;
    var next = (current + dir + thumbs.length) % thumbs.length;
    /* A real click: the legacy element/section listeners AND the delegated
       handler above run in the same order as a user tap on the thumb. */
    thumbs[next].click();
  }

  function syncArrows(root) {
    var host = hostOf(root);
    if (!host) return;
    var show = galleryThumbsOf(root).length > 1;
    var arrows = host.querySelectorAll('[data-mc-media-arrow]');
    for (var i = 0; i < arrows.length; i++) arrows[i].hidden = !show;
  }

  function observeArrows(root) {
    var host = hostOf(root);
    if (!host || host.mcMediaArrowsObserved) return;
    host.mcMediaArrowsObserved = true;
    if (typeof MutationObserver !== 'function') return;
    /* The 0028 app hydration appends component thumbs asynchronously —
       watch the strip so the arrows appear/disappear with the real count. */
    var strips = root.querySelectorAll(
      '[data-mc-media-thumbs], [data-mc-bundle-thumbs]'
    );
    for (var i = 0; i < strips.length; i++) {
      new MutationObserver(function () {
        syncArrows(root);
      }).observe(strips[i], { childList: true });
    }
  }

  /* ─── Autostart (DP-5) — off by default, deferred and conditional ─── */

  function initAutostart(root) {
    var host = hostOf(root);
    if (!host || host.mcMediaAutostartDone) return;
    host.mcMediaAutostartDone = true;

    if (host.getAttribute('data-mc-media-autostart') !== 'true') return;
    if (window.innerWidth < DESKTOP_MIN_WIDTH) return;
    if (prefersReducedMotion()) return;
    if (isDataSaving()) return;

    /* Hosted video only — `external_video` is never autostarted (DP-8.4). */
    var slide = root.querySelector('[data-mc-media-slide][data-media-type="video"]');
    if (!slide) return;

    var start = function () {
      var idle =
        window.requestIdleCallback ||
        function (callback) {
          return window.setTimeout(callback, IDLE_FALLBACK_MS);
        };
      idle(function () {
        var state = stateOf(root);
        if (state && state.userActed) return;
        if (root.querySelector('[data-mc-media-slide].is-active')) return;
        var mediaId = slide.getAttribute('data-media-id');
        var thumbs = root.querySelectorAll('[data-mc-media-thumb]');
        for (var i = 0; i < thumbs.length; i++) {
          if (thumbs[i].getAttribute('data-media-id') === String(mediaId)) {
            syncThumbs(root, thumbs[i]);
            break;
          }
        }
        /* focus:false — autostart never steals the focus (DP-13.7). */
        activate(root, mediaId, 'video', { focus: false, trigger: 'autostart' });
      });
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  /* ─── Init ─── */

  function init(scope) {
    var hosts = (scope || document).querySelectorAll('[data-mc-media-host]');
    for (var i = 0; i < hosts.length; i++) {
      var root = rootOf(hosts[i]);
      stateOf(root);
      /* Snapshot the server-rendered hero image BEFORE any interaction —
         if the first click is on a video thumb, the legacy handler has
         already replaced the hero src by the time our handler runs, so
         capturing lazily would capture the poster (sizing fix). */
      captureHeroImageOnce(root);
      syncPlayButton(root);
      syncArrows(root);
      observeArrows(root);
      initAutostart(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme Editor: re-initialise on section reload. The click/keydown
     listeners are delegated on `document`, so they are never duplicated. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
